import { Award, Link2, Megaphone } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { GradientText, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";
import { LinkGraph } from "./components/LinkGraph";

function Strong({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-white">{children}</span>;
}

interface Point {
  icon: LucideIcon;
  body: ReactNode;
}

const POINTS: readonly Point[] = [
  {
    icon: Link2,
    body: (
      <>
        Signals from <Strong>elsewhere</Strong> — <Strong>backlinks</Strong>,{" "}
        <Strong>brand mentions</Strong>, <Strong>citations</Strong>,{" "}
        <Strong>social proof</Strong>: anything showing the wider web{" "}
        <GradientText className="font-semibold">trusts</GradientText> or talks
        about you.
      </>
    ),
  },
  {
    icon: Award,
    body: (
      <>
        <GradientText className="font-semibold">
          Quality beats quantity
        </GradientText>
        : a few <Strong>relevant, editorial</Strong> links often beat many{" "}
        <Strong>irrelevant directories</Strong>.
      </>
    ),
  },
  {
    icon: Megaphone,
    body: (
      <>
        <Strong>Brand search</Strong> and <Strong>real-world reputation</Strong>{" "}
        spill into online mentions — <Strong>PR</Strong> and{" "}
        <Strong>product news</Strong> matter.
      </>
    ),
  },
];

function OffPageSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={3} title="Off-page SEO">
      <div className="grid w-full grid-cols-[1.05fr_1fr] items-center gap-14">
        <LinkGraph active={active} />

        <div>
          <RevealList active={active} delay={0.9} stagger={0.22} className="flex flex-col gap-9">
            {POINTS.map(({ icon: Icon, body }, i) => (
              <RevealItem key={i} className="flex items-start gap-5">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                  <Icon className="h-7 w-7 text-accent-amber" strokeWidth={1.75} />
                </span>
                <p className="text-2xl leading-relaxed text-white/90">{body}</p>
              </RevealItem>
            ))}
          </RevealList>

          <Reveal active={active} delay={2.1} className="mt-12 border-t border-white/10 pt-6">
            <p className="font-mono text-xl text-white/50">
              demo — all domains in the graph are invented
            </p>
          </Reveal>
        </div>
      </div>
    </SlideShell>
  );
}

export const offpage: SlideDef = {
  id: "c3-offpage",
  chapter: 3,
  title: "Off-page SEO",
  component: OffPageSlide,
};
